import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { useAuth } from '../context/AuthContext';
import TransportationCard from '../components/TransportationCard';
import CustomSelect from '../components/CustomSelect';
import useTransportationBookings from '../hooks/useTransportationBookings';
import { useDogs } from '../hooks/useDogs';
//import { calculateSizeTag } from '../utils/dogUtils';
import { normalizeText } from '../utils/dataUtils';
import '../styles/pages/happy-bus.css';

const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

// Cuándo empieza el viaje.
const getTripDate = (b) => {
    if (b.pickupTime) return new Date(b.pickupTime);
    if (b.deliveryTime) return new Date(b.deliveryTime);
    return null;
};

// Cuándo termina. Un viaje de ida y vuelta se recoge un día y se entrega otro:
// sigue vivo hasta la entrega, así que lo que decide si ya pasó es la última de
// sus dos fechas, no la primera. En los de una sola pierna ambas coinciden.
const getTripEndDate = (b) => {
    const times = [b.pickupTime, b.deliveryTime]
        .filter(Boolean)
        .map(t => new Date(t));
    if (!times.length) return null;
    return new Date(Math.max(...times.map(t => t.getTime())));
};

// La fecha con la que el viaje entra en la agenda: lo siguiente que falta por
// hacer. Si ya se recogió ayer y falta entregar hoy, el viaje es trabajo de
// hoy y va en el día de la entrega, no en el de la recogida.
const getTripAgendaDate = (b) => {
    const start = getTripDate(b);
    const end = getTripEndDate(b);
    if (!start || !end) return start || end;
    return startOfDay(start) < startOfDay(new Date()) ? end : start;
};

const getTripSearchFields = (b) => {
    const dogNames = b.dogs?.map(d => d.dog?.name || d.manualDogName || '').join(' ') || b.dog?.name || b.manualDogName || '';
    const owner = b.dogs?.[0]?.dog?.owners?.[0]?.name || b.dogs?.[0]?.manualOwnerName || b.dog?.owners?.[0]?.name || b.manualOwnerName || '';
    return { dogNames, owner };
};

const getDayLabel = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diff = Math.round((d - now) / 86400000);
    const cap = s => s.charAt(0).toUpperCase() + s.slice(1);
    const weekday = cap(d.toLocaleDateString('es-MX', { weekday: 'long' }));
    const day = d.getDate();
    const month = cap(d.toLocaleDateString('es-MX', { month: 'long' }));
    if (diff === 0) return `Hoy · ${weekday} ${day} de ${month}`;
    if (diff === 1) return `Mañana · ${weekday} ${day} de ${month}`;
    return `${weekday} ${day} de ${month}`;
};

const EMPTY_FORM = {
    selectedDogs: [], // Array of { dogId, manualDogName, manualOwnerName, manualOwnerPhone, breed, weight }
    tripType: 'round-trip',
    pickupTime: '',
    deliveryTime: '',
    pickupAddress: '',
    deliveryAddress: '',
    notes: '',
    status: 'scheduled',
    paymentStatus: 'pending'
};

const HappyBus = () => {
    const { role } = useAuth();
    const isDriver = role === 'support';

    const {
        scheduled,
        history,
        loading,
        error,
        createBooking,
        updateBooking,
        deleteBooking,
        clearError
    } = useTransportationBookings();

    const { dogs, loadDogs } = useDogs();

    const [activeTab, setActiveTab] = useState('scheduled');
    const [editingBooking, setEditingBooking] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Form state
    const [dogMode, setDogMode] = useState('select');
    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [errors, setErrors] = useState({});
    const [dogSearch, setDogSearch] = useState('');

    // Search dogs against the full dataset (not just the first page) as the user types
    useEffect(() => {
        const timer = setTimeout(() => {
            void loadDogs({ search: dogSearch, limit: 50 });
        }, 300);
        return () => clearTimeout(timer);
    }, [dogSearch, loadDogs]);
    const [manualEntry, setManualEntry] = useState({
        manualDogName: '',
        manualOwnerName: '',
        manualOwnerPhone: '',
        breed: '',
        weight: ''
    });

    const [historySearch, setHistorySearch] = useState('');
    const [scheduledSearch, setScheduledSearch] = useState('');

    const [historyPaymentFilter, setHistoryPaymentFilter] = useState('all');
    const [historyTripTypeFilter, setHistoryTripTypeFilter] = useState('all');
    const [historyStatusFilter, setHistoryStatusFilter] = useState('all');

    // Auto-clear messages
    useEffect(() => {
        if (message.text) {
            const t = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return () => clearTimeout(t);
        }
    }, [message]);

    // Pre-fill form when editing
    useEffect(() => {
        if (editingBooking) {
            const fmt = (d) => {
                if (!d) return '';
                const dt = new Date(d);
                const pad = n => String(n).padStart(2, '0');
                return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
            };
            
            const bookingDogs = editingBooking.dogs && editingBooking.dogs.length > 0 
                ? editingBooking.dogs.map(d => ({
                    dogId: d.dog?._id || d.dog || null,
                    manualDogName: d.manualDogName || '',
                    manualOwnerName: d.manualOwnerName || '',
                    manualOwnerPhone: d.manualOwnerPhone || '',
                    breed: d.breed || '',
                    weight: d.weight || ''
                }))
                : (editingBooking.dog || editingBooking.manualDogName ? [{
                    dogId: editingBooking.dog?._id || null,
                    manualDogName: editingBooking.manualDogName || '',
                    manualOwnerName: editingBooking.manualOwnerName || '',
                    manualOwnerPhone: editingBooking.manualOwnerPhone || '',
                    breed: editingBooking.breed || '',
                    weight: editingBooking.weight || ''
                }] : []);

            const isManual = bookingDogs.some(d => !d.dogId && d.manualDogName);
            setDogMode(isManual ? 'manual' : 'select');
            
            setFormData({
                selectedDogs: bookingDogs,
                tripType: editingBooking.tripType || 'round-trip',
                pickupTime: fmt(editingBooking.pickupTime),
                deliveryTime: fmt(editingBooking.deliveryTime),
                pickupAddress: editingBooking.pickupAddress || '',
                deliveryAddress: editingBooking.deliveryAddress || '',
                notes: editingBooking.notes || '',
                status: editingBooking.status || 'scheduled',
                paymentStatus: editingBooking.paymentStatus || 'pending'
            });
        }
    }, [editingBooking]);

    const resetForm = () => {
        setFormData({ ...EMPTY_FORM });
        setErrors({});
        setDogMode('select');
        setDogSearch('');
        setEditingBooking(null);
        setManualEntry({
            manualDogName: '',
            manualOwnerName: '',
            manualOwnerPhone: '',
            breed: '',
            weight: ''
        });
    };

    const handleModeSwitch = (mode) => {
        setDogMode(mode);
        setErrors({});
        setFormData(prev => ({ ...prev, selectedDogs: [] }));
    };

    const toggleDogSelection = (dog) => {
        setFormData(prev => {
            const isSelected = prev.selectedDogs.some(d => d.dogId === dog._id);
            if (isSelected) {
                return { ...prev, selectedDogs: prev.selectedDogs.filter(d => d.dogId !== dog._id) };
            } else {
                return {
                    ...prev,
                    selectedDogs: [...prev.selectedDogs, {
                        dogId: dog._id,
                        manualDogName: '',
                        manualOwnerName: '',
                        manualOwnerPhone: '',
                        breed: dog.breed || '',
                        weight: dog.weight || ''
                    }]
                };
            }
        });
        if (errors.selectedDogs) setErrors(prev => ({ ...prev, selectedDogs: '' }));
    };

    const handleManualChange = (e) => {
        const { name, value } = e.target;
        setManualEntry(prev => ({ ...prev, [name]: value }));
    };

    const addManualDog = () => {
        if (!manualEntry.manualDogName.trim()) {
            setErrors(prev => ({ ...prev, manualDogName: 'Nombre requerido' }));
            return;
        }
        setFormData(prev => ({
            ...prev,
            selectedDogs: [...prev.selectedDogs, { ...manualEntry, dogId: null }]
        }));
        setManualEntry({
            manualDogName: '',
            manualOwnerName: '',
            manualOwnerPhone: '',
            breed: '',
            weight: ''
        });
        setErrors(prev => ({ ...prev, manualDogName: '', selectedDogs: '' }));
    };

    const removeSelectedDog = (index) => {
        setFormData(prev => ({
            ...prev,
            selectedDogs: prev.selectedDogs.filter((_, i) => i !== index)
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const e = {};
        if (formData.selectedDogs.length === 0) e.selectedDogs = 'Selecciona al menos un perro';
        if (formData.tripType !== 'dropoff-only' && !formData.pickupTime) e.pickupTime = 'Hora de recogida requerida';
        if (formData.tripType !== 'pickup-only'  && !formData.deliveryTime) e.deliveryTime = 'Hora de entrega requerida';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const needsPickup   = formData.tripType !== 'dropoff-only';
        const needsDelivery = formData.tripType !== 'pickup-only';
        const toISO = (v) => v ? new Date(v).toISOString() : null;
        
        const payload = {
            dogs: formData.selectedDogs.map(d => ({
                ...d,
                weight: d.weight ? parseFloat(d.weight) : undefined
            })),
            tripType: formData.tripType,
            pickupTime:      needsPickup   ? toISO(formData.pickupTime)   : null,
            deliveryTime:    needsDelivery ? toISO(formData.deliveryTime) : null,
            pickupAddress:   needsPickup   ? formData.pickupAddress   : '',
            deliveryAddress: needsDelivery ? formData.deliveryAddress : '',
            notes: formData.notes,
            paymentStatus: formData.paymentStatus,
            ...(editingBooking && { status: formData.status })
        };

        try {
            clearError();
            if (editingBooking) {
                await updateBooking(editingBooking._id, payload);
                setMessage({ type: 'success', text: '¡Viaje actualizado con éxito!' });
            } else {
                await createBooking(payload);
                setMessage({ type: 'success', text: '¡Viaje programado con éxito!' });
            }
            resetForm();
            setActiveTab('scheduled');
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handleEdit = (booking) => {
        setEditingBooking(booking);
        setActiveTab('form');
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este viaje?')) return;
        try {
            clearError();
            await deleteBooking(id);
            setMessage({ type: 'success', text: 'Viaje eliminado.' });
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handleStatusUpdate = async (id, status) => {
        try {
            clearError();
            await updateBooking(id, { status });
            setMessage({ type: 'success', text: `Estado actualizado a: ${status}` });
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handlePaymentUpdate = async (id, paymentStatus) => {
        try {
            clearError();
            await updateBooking(id, { paymentStatus });
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const filteredHistory = history.filter(b => {
        if (historyPaymentFilter !== 'all' && b.paymentStatus !== historyPaymentFilter) return false;
        if (historyTripTypeFilter !== 'all' && b.tripType !== historyTripTypeFilter) return false;
        if (historyStatusFilter !== 'all' && b.status !== historyStatusFilter) return false;

        if (!historySearch) return true;
        const q = normalizeText(historySearch);
        const dogName = b.dog?.name || b.manualDogName || '';
        const owner = b.dog?.owners?.[0]?.name || b.manualOwnerName || '';
        return normalizeText(dogName).includes(q) || normalizeText(owner).includes(q);
    });

    const getMonthLabel = (date) => {
        if (!date) return 'Sin fecha';
        const d = new Date(date);
        const cap = s => s.charAt(0).toUpperCase() + s.slice(1);
        return cap(d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }));
    };

    const historyMonthGroups = (() => {
        const monthMap = new Map();
        [...filteredHistory]
            .sort((a, b) => (getTripDate(b) || 0) - (getTripDate(a) || 0))
            .forEach(b => {
                const d = getTripDate(b);
                const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : '__nodate__';
                if (!monthMap.has(key)) monthMap.set(key, { date: d, bookings: [] });
                monthMap.get(key).bookings.push(b);
            });
        return [...monthMap.entries()].map(([key, val]) => ({ key, ...val }));
    })();

    const filteredScheduled = scheduled.filter(b => {
        if (!scheduledSearch) return true;
        const q = normalizeText(scheduledSearch);
        const { dogNames, owner } = getTripSearchFields(b);
        return normalizeText(dogNames).includes(q) || normalizeText(owner).includes(q);
    });

    // Day-based grouping for scheduled tab
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const upcomingScheduled = filteredScheduled.filter(b => {
        const d = getTripEndDate(b);
        if (!d) return true;
        return startOfDay(d) >= todayStart;
    });

    const scheduledDayGroups = (() => {
        const dayMap = new Map();
        [...upcomingScheduled]
            .sort((a, b) => (getTripAgendaDate(a) || 0) - (getTripAgendaDate(b) || 0))
            .forEach(b => {
                const d = getTripAgendaDate(b);
                const key = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : '__nodate__';
                if (!dayMap.has(key)) dayMap.set(key, { date: d, bookings: [] });
                dayMap.get(key).bookings.push(b);
            });
        return [...dayMap.entries()].map(([key, val]) => ({ key, ...val }));
    })();

    // Past scheduled/in-transit trips shown in history tab. Un viaje de ida y
    // vuelta recogido ayer con entrega hoy no está atrasado: todavía le falta
    // su última pierna, por eso se mide contra la fecha de fin.
    const pastScheduledHistory = scheduled.filter(b => {
        const d = getTripEndDate(b);
        if (!d) return false;
        if (startOfDay(d) >= todayStart) return false;
        if (!historySearch) return true;
        const q = normalizeText(historySearch);
        const { dogNames, owner } = getTripSearchFields(b);
        return normalizeText(dogNames).includes(q) || normalizeText(owner).includes(q);
    }).sort((a, b) => (getTripEndDate(b) || 0) - (getTripEndDate(a) || 0));

    return (
        <div>
            <Navigation />
            <div className="hb-container">
                <div className="hb-tab-nav">
                    <button
                        className={`hb-tab-btn${activeTab === 'scheduled' ? ' active' : ''}`}
                        onClick={() => setActiveTab('scheduled')}
                    >
                        📅 Programados
                    </button>
                    {!isDriver && (
                        <button
                            className={`hb-tab-btn${activeTab === 'form' ? ' active' : ''}`}
                            onClick={() => { resetForm(); setActiveTab('form'); }}
                        >
                            ➕ Agregar viaje
                        </button>
                    )}
                    <button
                        className={`hb-tab-btn${activeTab === 'history' ? ' active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        🕓 Historial
                    </button>
                </div>

                <header className="hb-header">
                    <h1>🚐 Happy Bus</h1>
                    <p className="hb-subtitle">Gestión de transporte para perros</p>
                </header>

                {message.text && (
                    <div className={`message ${message.type}`}>{message.text}</div>
                )}
                {error && (
                    <div className="message error">{error}</div>
                )}

                {/* SCHEDULED TAB */}
                {activeTab === 'scheduled' && (
                    <div>
                        <div className="hb-section-header">
                            <h2>Viajes programados</h2>
                            <span className="hb-count">{upcomingScheduled.length} viaje{upcomingScheduled.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="hb-search-bar">
                            <input
                                type="text"
                                placeholder="Buscar por perro o dueño..."
                                value={scheduledSearch}
                                onChange={e => setScheduledSearch(e.target.value)}
                            />
                        </div>
                        {loading && <div className="hb-loading">Cargando...</div>}
                        {!loading && upcomingScheduled.length === 0 && (
                            <div className="hb-empty">
                                <span className="hb-empty-icon">🚐</span>
                                <p>No hay viajes programados.</p>
                                {!isDriver && (
                                    <button className="hb-cta-btn" onClick={() => setActiveTab('form')}>
                                        + Agregar viaje
                                    </button>
                                )}
                            </div>
                        )}
                        {scheduledDayGroups.map(({ key, date, bookings }) => {
                            const isToday = date && new Date(date).setHours(0,0,0,0) === todayStart.getTime();
                            return (
                                <div key={key} className="hb-day-section">
                                    <div className={`hb-day-header${isToday ? ' hb-day-header--today' : ''}`}>
                                        <span className="hb-day-label">
                                            {date ? getDayLabel(date) : 'Sin fecha asignada'}
                                        </span>
                                        <span className="hb-day-count">{bookings.length} viaje{bookings.length !== 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="hb-grid">
                                        {bookings.map(b => (
                                            <TransportationCard
                                                key={b._id}
                                                booking={b}
                                                onEdit={isDriver ? undefined : handleEdit}
                                                onDelete={isDriver ? undefined : handleDelete}
                                                onStatusUpdate={handleStatusUpdate}
                                                onPaymentUpdate={isDriver ? undefined : handlePaymentUpdate}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* FORM TAB */}
                {activeTab === 'form' && (
                    <div className="hb-form-wrapper">
                        <h2>{editingBooking ? 'Editar viaje' : 'Nuevo viaje'}</h2>
                        <form onSubmit={handleSubmit} className="hb-form">

                            {/* Dog selector */}
                            <div className="form-group full-width">
                                <label>Perros *</label>
                                {!editingBooking && (
                                    <div className="dog-mode-toggle">
                                        <button type="button" className={`dog-mode-btn${dogMode === 'select' ? ' active' : ''}`} onClick={() => handleModeSwitch('select')}>
                                            🐕 Seleccionar de la lista
                                        </button>
                                        <button type="button" className={`dog-mode-btn${dogMode === 'manual' ? ' active' : ''}`} onClick={() => handleModeSwitch('manual')}>
                                            ✍️ Ingresar manualmente
                                        </button>
                                    </div>
                                )}

                                {formData.selectedDogs.length > 0 && (
                                    <div className="selected-dogs-list">
                                        <p className="selected-dogs-title">Perros en este viaje:</p>
                                        <div className="selected-dogs-grid">
                                            {formData.selectedDogs.map((d, index) => {
                                                const dogInfo = d.dogId ? dogs.find(dog => dog._id === d.dogId) : null;
                                                return (
                                                    <div key={index} className="selected-dog-tag">
                                                        <span className="selected-dog-name">
                                                            {dogInfo ? dogInfo.name : d.manualDogName}
                                                        </span>
                                                        <button type="button" className="remove-dog-btn" onClick={() => removeSelectedDog(index)}>✕</button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {dogMode === 'select' ? (
                                    <>
                                        <input
                                            type="text"
                                            className="dog-picker-search"
                                            placeholder="Buscar perro o dueño..."
                                            value={dogSearch}
                                            onChange={e => setDogSearch(e.target.value)}
                                        />
                                        <div className="dog-picker-list">
                                            {dogs.map(dog => {
                                                const isSelected = formData.selectedDogs.some(d => d.dogId === dog._id);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={dog._id}
                                                        className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                                        onClick={() => toggleDogSelection(dog)}
                                                    >
                                                        <div className="dog-picker-avatar">
                                                            {dog.pictureUrl || dog.picture
                                                                ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                                                : <span>{dog.name?.[0]?.toUpperCase()}</span>
                                                            }
                                                        </div>
                                                        <div className="dog-picker-info">
                                                            <div className="dog-picker-name">{dog.name}</div>
                                                            <div className="dog-picker-breed">{dog.owners?.map(o => o.name).join(', ')}</div>
                                                        </div>
                                                        {isSelected && <span className="dog-picker-check">✓</span>}
                                                    </button>
                                                );
                                            })}
                                            {dogs.length === 0 && (
                                                <div className="dog-picker-empty">No se encontraron perros</div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="manual-dog-form-add">
                                        <div className="manual-dog-fields-grid">
                                            <input type="text" name="manualDogName" value={manualEntry.manualDogName} onChange={handleManualChange} placeholder="Nombre del perro *" />
                                            <input type="text" name="manualOwnerName" value={manualEntry.manualOwnerName} onChange={handleManualChange} placeholder="Nombre del dueño" />
                                            <input type="tel" name="manualOwnerPhone" value={manualEntry.manualOwnerPhone} onChange={handleManualChange} placeholder="Teléfono" />
                                            <input type="text" name="breed" value={manualEntry.breed} onChange={handleManualChange} placeholder="Raza" />
                                            <input type="number" name="weight" value={manualEntry.weight} onChange={handleManualChange} placeholder="Peso (kg)" step="0.1" />
                                            <button type="button" className="hb-btn-secondary" onClick={addManualDog}>+ Agregar perro</button>
                                        </div>
                                        {errors.manualDogName && <div className="error">{errors.manualDogName}</div>}
                                    </div>
                                )}
                                {errors.selectedDogs && <div className="error">{errors.selectedDogs}</div>}
                            </div>

                            {/* Trip type */}
                            <div className="form-group full-width">
                                <label>Tipo de servicio</label>
                                <div className="hb-trip-type-group">
                                    {[
                                        { value: 'round-trip',   label: '🔄 Ida y vuelta' },
                                        { value: 'pickup-only',  label: '📍 Solo recogida' },
                                        { value: 'dropoff-only', label: '🏠 Solo entrega' },
                                    ].map(opt => (
                                        <label key={opt.value} className={`hb-trip-type-btn${formData.tripType === opt.value ? ' active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="tripType"
                                                value={opt.value}
                                                checked={formData.tripType === opt.value}
                                                onChange={handleChange}
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Pickup — hidden for dropoff-only */}
                            {formData.tripType !== 'dropoff-only' && (
                                <>
                                <div className="form-group">
                                    <label>📍 Hora de recogida *</label>
                                    <input
                                        type="datetime-local"
                                        name="pickupTime"
                                        value={formData.pickupTime}
                                        onChange={handleChange}
                                    />
                                    {errors.pickupTime && <div className="error">{errors.pickupTime}</div>}
                                </div>
                                <div className="form-group">
                                    <label>Dirección de recogida</label>
                                    <input
                                        type="text"
                                        name="pickupAddress"
                                        value={formData.pickupAddress}
                                        onChange={handleChange}
                                        placeholder="Calle, colonia..."
                                    />
                                </div>
                                </>
                            )}

                            {/* Delivery — hidden for pickup-only */}
                            {formData.tripType !== 'pickup-only' && (
                                <>
                                <div className="form-group">
                                    <label>🏠 Hora de entrega *</label>
                                    <input
                                        type="datetime-local"
                                        name="deliveryTime"
                                        value={formData.deliveryTime}
                                        onChange={handleChange}
                                    />
                                    {errors.deliveryTime && <div className="error">{errors.deliveryTime}</div>}
                                </div>
                                <div className="form-group">
                                    <label>Dirección de entrega</label>
                                    <input
                                        type="text"
                                        name="deliveryAddress"
                                        value={formData.deliveryAddress}
                                        onChange={handleChange}
                                        placeholder="Calle, colonia..."
                                    />
                                </div>
                                </>
                            )}

                            {/* Payment status */}
                            <div className="form-group">
                                <label>Pago</label>
                                <CustomSelect
                                    name="paymentStatus"
                                    value={formData.paymentStatus}
                                    onChange={handleChange}
                                    options={[
                                        { value: 'pending', label: 'Pendiente' },
                                        { value: 'paid',    label: 'Pagado' },
                                        { value: 'package', label: 'Paquete' }
                                    ]}
                                />
                            </div>

                            {/* Status (edit only) */}
                            {editingBooking && (
                                <div className="form-group">
                                    <label>Estado</label>
                                    <CustomSelect
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        options={[
                                            { value: 'scheduled',  label: 'Programado' },
                                            { value: 'in-transit', label: 'En camino' },
                                            { value: 'completed',  label: 'Completado' },
                                            { value: 'cancelled',  label: 'Cancelado' }
                                        ]}
                                    />
                                </div>
                            )}

                            {/* Notes */}
                            <div className="form-group full-width">
                                <label>Notas</label>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Instrucciones especiales, indicaciones..."
                                />
                            </div>

                            <div className="hb-form-actions">
                                <button type="submit" className="hb-btn-primary" disabled={loading}>
                                    {loading ? 'Guardando...' : editingBooking ? 'Actualizar viaje' : 'Programar viaje'}
                                </button>
                                <button
                                    type="button"
                                    className="hb-btn-secondary"
                                    onClick={() => { resetForm(); setActiveTab('scheduled'); }}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* HISTORY TAB */}
                {activeTab === 'history' && (
                    <div>
                        <div className="hb-section-header">
                            <h2>Historial de viajes</h2>
                            <span className="hb-count">{filteredHistory.length + pastScheduledHistory.length} registro{(filteredHistory.length + pastScheduledHistory.length) !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="hb-search-bar">
                            <input
                                type="text"
                                placeholder="Buscar por perro o dueño..."
                                value={historySearch}
                                onChange={e => setHistorySearch(e.target.value)}
                            />
                        </div>
                        {loading && <div className="hb-loading">Cargando...</div>}
                        {!loading && filteredHistory.length === 0 && pastScheduledHistory.length === 0 && (
                            <div className="hb-empty">
                                <span className="hb-empty-icon">📋</span>
                                <p>No hay viajes en el historial.</p>
                            </div>
                        )}
                        {pastScheduledHistory.length > 0 && (
                            <div className="hb-day-section">
                                <div className="hb-past-header">
                                    <span className="hb-past-label">⚠️ Viajes pasados sin completar</span>
                                    <span className="hb-day-count">{pastScheduledHistory.length}</span>
                                </div>
                                <div className="hb-grid">
                                    {pastScheduledHistory.map(b => (
                                        <TransportationCard
                                            key={b._id}
                                            booking={b}
                                            onEdit={isDriver ? undefined : handleEdit}
                                            onDelete={isDriver ? undefined : handleDelete}
                                            onStatusUpdate={handleStatusUpdate}
                                            onPaymentUpdate={isDriver ? undefined : handlePaymentUpdate}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="hb-filter-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                            {[{ key: 'all', label: 'Todos' }, { key: 'paid', label: 'Pagados' }, { key: 'pending', label: 'Pendientes' }, { key: 'package', label: 'Paquete' }].map(f => (
                                <button key={`pay-${f.key}`} className={`pos-chip${historyPaymentFilter === f.key ? ' active' : ''}`} onClick={() => setHistoryPaymentFilter(f.key)}>{f.label}</button>
                            ))}
                            <div style={{ width: '1px', background: '#e0e0e0', margin: '0 4px' }}></div>
                            {[{ key: 'all', label: 'Cualquier viaje' }, { key: 'round-trip', label: 'Ida y vuelta' }, { key: 'pickup-only', label: 'Recogida' }, { key: 'dropoff-only', label: 'Entrega' }].map(f => (
                                <button key={`trip-${f.key}`} className={`pos-chip${historyTripTypeFilter === f.key ? ' active' : ''}`} onClick={() => setHistoryTripTypeFilter(f.key)}>{f.label}</button>
                            ))}
                            <div style={{ width: '1px', background: '#e0e0e0', margin: '0 4px' }}></div>
                            {[{ key: 'all', label: 'Cualquier estado' }, { key: 'completed', label: 'Completado' }, { key: 'cancelled', label: 'Cancelado' }].map(f => (
                                <button key={`stat-${f.key}`} className={`pos-chip${historyStatusFilter === f.key ? ' active' : ''}`} onClick={() => setHistoryStatusFilter(f.key)}>{f.label}</button>
                            ))}
                        </div>

                        {historyMonthGroups.map(({ key, date, bookings }) => (
                            <div key={key} className="hb-day-section">
                                <div className="hb-day-header">
                                    <span className="hb-day-label">{getMonthLabel(date)}</span>
                                    <span className="hb-day-count">{bookings.length} viaje{bookings.length !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="hb-grid">
                                    {bookings.map(b => (
                                        <TransportationCard
                                            key={b._id}
                                            booking={b}
                                            onEdit={isDriver ? undefined : handleEdit}
                                            onDelete={isDriver ? undefined : handleDelete}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HappyBus;
